<script setup>
import { computed, ref, watch } from 'vue';

import { HrChip, HrMenu } from '../primitives';
import HrMenuItem from './HrMenuItem.vue';

/**
 * Menu chip — a low-emphasis chip showing the current choice, which opens a menu to change it.
 *
 * NAMED FOR THE MECHANISM, NOT A SEMANTIC CATEGORY. M3 defines exactly four chip types by function:
 * assist, filter, input and suggestion. This is none of them, so it says what it does rather than
 * asserting a fifth category the spec does not have and a reader of the M3 docs would not find.
 *
 * IT WEARS THE FILTER CHIP'S CLOTHES AND IS NOT ONE. M3 draws the dropdown chip as a filter chip, so
 * it reuses that variant's tokens rather than adding a sixth entry to `variant` — the variant names
 * the M3 visual family, this component names the use. What differs is meaning: a filter chip's
 * selected state is view state and its `modelValue` says "this filter is on", where this chip's value
 * is a real choice its caller usually persists and "selected" says "not the usual answer".
 *
 * TWO STATES, AND THE COMPONENT OWNS BOTH SO THEY CANNOT DRIFT. The caret follows whether the menu is
 * open; the container fill follows whether the choice is non-default. A single flag driving both is
 * the trap: a chip showing a non-default choice would sit with a permanently open caret. Menu-open is
 * derived internally rather than passed in, so a caller has nothing to keep in sync.
 *
 * THE EMPHASIS IS ASYMMETRIC ON PURPOSE. Where one option is overwhelmingly the norm, the chip should
 * be quiet in that state and unmissable in any other, so someone reopening a record can see at a
 * glance why the form in front of them looks different. That is what `defaultValue` is for; without
 * it every choice reads as equally remarkable, which for a lopsided distribution is backwards.
 *
 * @status recommended
 * @group chips
 * @useWhen One choice among a few qualifies the content below it, and one of them is the usual answer
 * @avoidWhen Toggling a criterion on and off — use HrFilterChip
 * @avoidWhen The choice is a form field the user is entering rather than a qualifier — use HrListSelect
 * @related HrFilterChip
 * @related HrMenuItem
 * @a11y The chip is a button that opens a menu; items are reachable by keyboard and the menu closes on select.
 */
defineOptions({ name: 'HrMenuChip' });

/**
 * @typedef {{ label: string, value: *, caption?: string, disable?: boolean }} HrMenuChipOption
 */

const props = defineProps({
  /** The chosen value. */
  modelValue: {
    type: null,
    default: null,
  },
  /**
   * The options to choose from. `caption` becomes the item's second line, which is where an
   * explanation belongs: the person choosing reads it when it matters, and nobody else has to.
   * @type {HrMenuChipOption[]}
   * @example [{"label":"Normal","value":"normal"},{"label":"Training","value":"training","caption":"Trains staff."}]
   */
  options: {
    type: Array,
    default: () => [],
  },
  /**
   * The value that counts as the usual answer. The chip stays low emphasis while the choice matches
   * it, and takes the selected fill for anything else. Leave unset to treat every choice alike.
   */
  defaultValue: {
    type: null,
    default: null,
  },
  /** Prevents interaction and dims the chip. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when an option is chosen; payload is that option's value. */
  'update:modelValue': null,
  /** Fired when the menu opens. Use it to load what the menu lists, rather than loading it up front. */
  show: null,
  /** Fired when the menu closes. */
  hide: null,
});

const isOpen = ref(false);

watch(isOpen, (open) => emit(open ? 'show' : 'hide'));

const selectedOption = computed(
  () => props.options.find((option) => option.value === props.modelValue) ?? null,
);

const label = computed(() => selectedOption.value?.label ?? null);

// Only meaningful once a default has been named; otherwise every choice is equally ordinary.
const isNonDefault = computed(
  () => props.defaultValue !== null && props.modelValue !== props.defaultValue,
);

function onSelect(option) {
  if (option.disable) return;
  emit('update:modelValue', option.value);
  isOpen.value = false;
}
</script>

<template>
  <hr-chip
    :label="label"
    :selected="isNonDefault"
    :disable="disable"
    :icon-right="isOpen ? 'arrow_drop_up' : 'arrow_drop_down'"
    variant="filter"
    clickable
    class="hr-menu-chip"
  >
    <hr-menu
      v-model="isOpen"
      :disable="disable"
      anchor="bottom start"
      self="top start"
    >
      <hr-menu-item
        v-for="option in options"
        :key="String(option.value)"
        :label="option.label"
        :caption="option.caption ?? null"
        :disable="option.disable ?? false"
        @click="onSelect(option)"
      />
    </hr-menu>
  </hr-chip>
</template>
